import { useReducer, useEffect } from 'react';

import { useGoogleAnalyticsErrorContext } from '@hydra/common/context';
import { useSyncThirdParty } from '@hydra/common/customHooks';
import * as fetcher from '@hydra/utils/fetcher';

interface GoogleAnalyticsAuth {
  url: string
  authenticated: boolean
  invalidGrant: boolean
  propertyId?: string
  accessToken?: string
}

type GoogleAnalyticsAuthAction = { type: 'UPDATE_GA', authenticated: boolean, invalidGrant: boolean }
  | { type: 'UPDATE_TOKEN', propertyId: string, accessToken: string }

function googleAnalyticsReducer (state: GoogleAnalyticsAuth, action: GoogleAnalyticsAuthAction) {
  switch (action.type) {
    case 'UPDATE_GA':
      return {
        ...state,
        authenticated: action.authenticated,
        invalidGrant: action.invalidGrant
      };
    case 'UPDATE_TOKEN':
      return {
        ...state,
        propertyId: action.propertyId,
        accessToken: action.accessToken
      };
    default:
      return state;
  }
}

function init (googleAnalytcisData: GoogleAnalyticsAuth) {
  return {
    ...googleAnalytcisData
  };
}

function useGA4Auth (props: GoogleAnalyticsAuth) {
  const [{
    url,
    authenticated,
    invalidGrant,
    propertyId,
    accessToken
  }, dispatch] = useReducer(googleAnalyticsReducer, props, init);
  const { setGaError } = useGoogleAnalyticsErrorContext();

  useEffect(() => {
    const fetchGaToken = async () => {
      const projectUrl = `https://${url}`;
      const urlToAnalyze = new URL(projectUrl).host.replace(/^www\./, '');
      const endpoint = '/api/third-party/token?isFour=1&url=' + encodeURIComponent(urlToAnalyze);
      try {
        const res = await fetcher.get(endpoint);
        if (res.status === 200) {
          const jsonRes = await res.json();
          dispatch({
            type: 'UPDATE_TOKEN',
            ...jsonRes
          });
        } else {
          setGaError(res.status);
        }
      } catch (err) {
        setGaError(err.code);
      }
    };
    if (authenticated && !propertyId && !accessToken) {
      fetchGaToken();
    }
  }, [authenticated, propertyId, accessToken, url, setGaError]);

  const { handleSyncAccount } = useSyncThirdParty({
    thirdPartyAccount: 'GA4Data',
    projectUrl: url,
    updateData: data => {
      dispatch({
        type: 'UPDATE_GA',
        authenticated: data.authenticated,
        invalidGrant: data.invalidGrant
      });
    }
  });

  return {
    handleSyncAccount,
    authenticated,
    invalidGrant,
    accessToken,
    propertyId
  };
}

export default useGA4Auth;
